import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import './Modal.css';

export function Modal({ 
  isOpen,
  onClose,
  title,
  children,
  footer,
  size = 'md',
  showCloseButton = true,
  className = '',
  ...props
}) {
  const { t } = useTranslation();

  // Handle ESC key
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [isOpen, onClose]);

  // Lock body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const modalClasses = classNames(
    'agrisense-modal__container',
    `agrisense-modal__container--${size}`,
    className
  );

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const modalContent = (
    <div className="agrisense-modal" onClick={handleBackdropClick}>
      {/* Backdrop - visual only, click handled by parent */}
      <div 
        className="agrisense-modal__backdrop" 
        data-testid="modal-backdrop"
      />
      
      {/* Modal Container */}
      <div 
        className={modalClasses}
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
        data-testid="modal"
        onClick={(e) => e.stopPropagation()}
        {...props}
      >
        {/* Header */}
        <div className="agrisense-modal__header">
          <h2 id="modal-title" className="agrisense-modal__title">{title}</h2>
          {showCloseButton && (
            <button 
              className="agrisense-modal__close"
              onClick={onClose}
              aria-label={t('common.a11y.closeModal')}
              data-testid="modal-close"
            >
              <X size={20} />
            </button>
          )}
        </div>
        
        {/* Body */}
        <div className="agrisense-modal__body">
          {children}
        </div>
        
        {/* Footer */}
        {footer && (
          <div className="agrisense-modal__footer">
            {footer}
          </div>
        )}
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}
